/**
 * Wires the pieces together: screen flow, the fixed-step loop, input, the
 * `Match`, its renderer, and the presentation feel (particles, screen shake,
 * synthesised SFX + music).
 */

import { CanvasRenderer, type RenderOverlay } from "../render/canvas";
import { TrailTracker } from "../render/trail-tracker";
import { ParticleField } from "../render/particles";
import { ScreenShake } from "../render/effects";
import { KeyboardController } from "../input/keyboard";
import { GamepadController } from "../input/gamepad";
import { Match, NO_INPUT, type PlayerInput } from "../sim/match";
import { COLOR_HEX } from "../sim/types";
import { Sfx } from "../audio/sfx";
import { Music } from "../audio/music";
import { GameLoop } from "./loop";
import { nextState, type AppState, type FlowEvent } from "./flow";
import { COUNTDOWN_SECONDS, DEFAULT_CONFIG } from "./config";
import { Screens, type ScreenIntent } from "../ui/screens";
import { Toggles, type ToggleKey, type ToggleState } from "../ui/toggles";

const TICK_SECONDS = DEFAULT_CONFIG.tickSeconds;
const PREFS_KEY = "tron:prefs";
const DEFAULT_PREFS: ToggleState = { music: false, sfx: true, crt: false };

function loadPrefs(): ToggleState {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const p = JSON.parse(raw) as Partial<ToggleState>;
    return {
      music: typeof p.music === "boolean" ? p.music : DEFAULT_PREFS.music,
      sfx: typeof p.sfx === "boolean" ? p.sfx : DEFAULT_PREFS.sfx,
      crt: typeof p.crt === "boolean" ? p.crt : DEFAULT_PREFS.crt,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function savePrefs(s: Readonly<ToggleState>): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(s));
  } catch {
    // Private mode / storage disabled — preferences just won't persist.
  }
}

export class App {
  private state: AppState = "title";
  private match: Match | null = null;
  private countdownTicks = 0;
  private lastCountdownSecond = -1;
  private crt: boolean;
  private wantMusic: boolean;
  private audioUnlocked = false;
  private lastRenderMs = performance.now();

  private readonly canvas: HTMLCanvasElement;
  private readonly trails = new TrailTracker();
  private readonly particles = new ParticleField();
  private readonly shake = new ScreenShake();
  private readonly renderer: CanvasRenderer;
  private readonly keyboard = new KeyboardController();
  private readonly gamepad = new GamepadController();
  private readonly sfx = new Sfx();
  private readonly music = new Music();
  private readonly screens: Screens;
  private readonly toggles: Toggles;
  private readonly loop: GameLoop;
  private readonly countdownTotalTicks = Math.round(COUNTDOWN_SECONDS / TICK_SECONDS);

  constructor(canvas: HTMLCanvasElement, appEl: HTMLElement) {
    this.canvas = canvas;
    const prefs = loadPrefs();
    this.crt = prefs.crt;
    this.wantMusic = prefs.music;
    this.sfx.muted = !prefs.sfx;

    this.renderer = new CanvasRenderer(canvas, DEFAULT_CONFIG.grid);
    this.screens = new Screens(appEl);
    this.screens.onIntent = (intent) => this.handleIntent(intent);

    this.toggles = new Toggles(document.body, prefs);
    this.toggles.onChange = (key, value) => this.applyToggle(key, value);
    const reposition = (): void => this.positionToggles();
    window.addEventListener("resize", reposition);
    new ResizeObserver(reposition).observe(canvas);
    reposition();
    requestAnimationFrame(reposition);

    this.keyboard.attach();
    window.addEventListener("keydown", (e) => this.onKey(e));
    const unlock = (): void => this.unlockAudio();
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("pointerdown", unlock, { once: true });

    this.loop = new GameLoop(TICK_SECONDS, {
      update: () => this.update(),
      render: (alpha) => this.render(alpha),
    });

    this.screens.show("title", null);

    // Debug handle for the browser console / smoke tests.
    (globalThis as unknown as { __tron?: unknown }).__tron = {
      state: () => this.state,
      match: () => this.match,
      pump: (n: number) => {
        for (let i = 0; i < n; i++) this.update();
      },
      draw: () => this.render(0),
      pause: () => this.loop.stop(),
      resume: () => this.loop.start(),
    };
  }

  start(): void {
    this.loop.start();
  }

  private unlockAudio(): void {
    if (this.audioUnlocked) return;
    this.audioUnlocked = true;
    this.sfx.unlock();
    if (this.wantMusic && !this.music.playing) this.music.start();
  }

  /** Keep the toggle stack pinned to the bottom of the scoreboard panel. */
  private positionToggles(): void {
    const r = this.canvas.getBoundingClientRect();
    if (r.width === 0) return;
    const scale = r.width / this.renderer.width;
    const pad = 16 * scale;
    const panelLeft = r.left + this.renderer.arenaWidth * scale;
    const panelWidth = (this.renderer.width - this.renderer.arenaWidth) * scale;
    this.toggles.place(panelLeft + pad, r.bottom - pad - 108 * scale, panelWidth - pad * 2);
  }

  private applyToggle(key: ToggleKey, value: boolean): void {
    switch (key) {
      case "music":
        this.wantMusic = value;
        if (value) this.music.start();
        else this.music.stop();
        break;
      case "sfx":
        this.sfx.muted = !value;
        break;
      case "crt":
        this.crt = value;
        break;
    }
    savePrefs(this.toggles.get());
  }

  // --- flow ---------------------------------------------------------------

  private dispatch(ev: FlowEvent): void {
    const next = nextState(this.state, ev);
    if (next !== this.state) this.enter(next);
  }

  private enter(next: AppState): void {
    const prev = this.state;
    this.state = next;

    switch (next) {
      case "title":
      case "setup":
        this.match = null;
        this.trails.reset();
        this.particles.clear();
        this.shake.reset();
        break;
      case "countdown":
        if (prev === "roundOver") {
          this.match?.nextRound();
          this.trails.reset();
        }
        this.particles.clear();
        this.shake.reset();
        this.countdownTicks = this.countdownTotalTicks;
        this.lastCountdownSecond = -1;
        break;
      case "gameOver":
        this.match?.nextRound(); // sets phase = 'gameOver' and matchWinner
        this.sfx.win();
        break;
      case "playing":
      case "roundOver":
        break;
    }

    this.screens.show(next, this.match);
  }

  private handleIntent(intent: ScreenIntent): void {
    switch (intent.type) {
      case "toTitle":
        this.dispatch({ type: "toTitle" });
        break;
      case "start": {
        this.match = new Match({
          playerCount: intent.settings.players,
          humanCount: intent.settings.humans,
          aiLevel: intent.settings.aiLevel,
          seed: Date.now() >>> 0,
          config: { ...DEFAULT_CONFIG, victoryScore: intent.settings.victoryScore },
        });
        this.trails.reset();
        this.dispatch({ type: "advance" }); // setup -> countdown
        break;
      }
      case "advance":
        this.onAdvance();
        break;
    }
  }

  private onAdvance(): void {
    const matchDecided = this.state === "roundOver" && (this.match?.isDecided() ?? false);
    this.dispatch({ type: "advance", matchDecided });
  }

  private onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      if (this.state !== "title" && this.state !== "setup") {
        this.dispatch({ type: "toTitle" });
      }
      return;
    }
    if (e.key !== "Enter") return;
    if (this.state === "setup") {
      this.handleIntent({ type: "start", settings: { ...this.screens.settings } });
    } else if (this.state === "title" || this.state === "roundOver" || this.state === "gameOver") {
      this.onAdvance();
    }
  }

  // --- loop --------------------------------------------------------------

  private update(): void {
    const kb = this.keyboard.poll(); // always drain, even when not playing

    if (this.state === "countdown") {
      this.countdownTicks -= 1;
      const sec = Math.ceil(this.countdownTicks * TICK_SECONDS);
      if (sec !== this.lastCountdownSecond) {
        this.lastCountdownSecond = sec;
        if (sec > 0) this.sfx.beep();
      }
      if (this.countdownTicks <= 0) {
        this.sfx.go();
        this.dispatch({ type: "countdownDone" });
      }
      return;
    }

    if (this.state !== "playing" || !this.match) return;

    const pad = this.gamepad.poll(this.match.playerCount);
    const inputs: PlayerInput[] = Array.from(
      { length: this.match.playerCount },
      (_, i): PlayerInput => {
        const k = kb[i] ?? NO_INPUT;
        const g = pad[i] ?? NO_INPUT;
        return { turn: k.turn ?? g.turn, useBonus: k.useBonus || g.useBonus };
      },
    );
    this.match.tick(inputs);
    this.trails.sample(this.match);
    this.consumeEvents();

    if (this.match.phase === "roundOver") this.dispatch({ type: "roundEnded" });
  }

  private consumeEvents(): void {
    if (!this.match) return;
    for (const ev of this.match.frameEvents) {
      switch (ev.kind) {
        case "death":
          this.particles.burst(ev.x, ev.y, COLOR_HEX[ev.color][0]);
          this.shake.add(0.55);
          this.sfx.crash();
          break;
        case "bonus":
          this.sfx.pickup();
          break;
        case "clean":
          this.sfx.boost();
          this.shake.add(0.25);
          break;
        case "roundEnd":
          break;
      }
    }
  }

  private render(alpha: number): void {
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastRenderMs) / 1000);
    this.lastRenderMs = now;

    this.particles.update(dt);
    const { x: shakeX, y: shakeY } = this.shake.sample(dt);

    const overlay: RenderOverlay = {
      shakeX,
      shakeY,
      crt: this.crt,
      particles: this.particles,
    };
    if (this.state === "countdown") {
      overlay.countdown = Math.max(0, this.countdownTicks * TICK_SECONDS);
    }
    if (this.state === "roundOver" || this.state === "gameOver") {
      overlay.dim = true;
    }
    this.renderer.render(this.match, this.trails, alpha, overlay);
  }
}
